import React, { FC, useContext } from 'react'

import Title from '../../common/Title/Title'
import TopBar from '../TopBar/TopBar'

import Link from 'next/link'
import sanitizeHtml from 'sanitize-html'

import { journalItem_element } from '../../../__generated__/journalItem'
import css from './JournalDetailPost.module.scss'
import Share from '../../common/Share/Share'
import MaxWidth from '../../layout/MaxWidth/MaxWidth'
import {
  getDateString,
  getImageSrc,
  getOptimizedImageSrc,
} from '../../../lib/utils'
import ReadMore from '../ReadMore/ReadMore'
import { ArticleContext } from '../../../contexts/articleContext'
import cx from 'classnames'
import { createCatalogElement } from '../../../lib/createCatalogItem'
import Card from '../../catalog/Card/Card'

interface IJournalDetailPostProps {
  post: journalItem_element
}

const JournalDetailPost: FC<IJournalDetailPostProps> = ({ post }) => {
  const { section, code, sections } = useContext(ArticleContext)
  const now = new Date().getTime()

  const products = post?.propertyValuesRelation
    ?.find((val) => val.property.code === 'PROPDUCTS')
    ?.targetElements?.filter((el) => {
      if (el.active) {
        if (el.activeFrom && el.activeTo) {
          const from = new Date(el.activeFrom).getTime()
          const to = new Date(el.activeTo).getTime()

          return now >= from && now <= to
        } else if (el.activeFrom && !el.activeTo) {
          const from = new Date(el.activeFrom).getTime()

          return now >= from
        } else if (!el.activeFrom && el.activeTo) {
          const to = new Date(el.activeTo).getTime()
          return now <= to
        }

        return true
      } else {
        return false
      }
    })
    ?.map((element) => createCatalogElement(element))

  const link = `journal/${section}/${code}`

  return (
    <section>
      <TopBar sections={sections} />

      <MaxWidth>
        <div className={css.heading}>
          <Link href="/journal" passHref>
            <a className="brown-link">← Журнал</a>
          </Link>

          <Title>{post.name}</Title>
        </div>
        <div className={css.info}>
          <div className={css.category}>
            <h5 className={css.sidebar_heading}>Тематика</h5>
            <p className={css.sidebar_text}>{post.sections[0].name}</p>
          </div>
          <div className={css.date}>
            <h5 className={css.sidebar_heading}>Дата публикации</h5>
            <p className={css.sidebar_text}>
              {getDateString(new Date(post.activeFrom))}
            </p>
          </div>
        </div>
      </MaxWidth>
      <MaxWidth mobileWide>
        <div className={css.post_wrapper}>
          <div className={css.post}>
            <figure className={css.cover}>
              <img
                src={getOptimizedImageSrc(
                  post?.detailPicture?.url || post?.previewPicture?.url,
                  75,
                )}
                alt={post?.name}
              />
            </figure>
            <div
              className={cx('content', css.content)}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(post.detailText, {
                  allowedTags: [
                    'div',
                    'span',
                    'p',
                    'a',
                    'img',
                    'h2',
                    'h3',
                    'h4',
                    'h5',
                    'h6',
                    'ol',
                    'ul',
                    'li',
                    'figure',
                    'figcaption',
                    'iframe',
                    'b',
                    'i',
                    'br',
                  ],
                  allowedAttributes: false,
                  transformTags: {
                    img: (tagName, attrs) => {
                      attrs.src = getImageSrc(attrs.src)
                      return { tagName: 'img', attribs: attrs }
                    },
                  },
                }),
              }}
            ></div>
          </div>
          <aside className={css.sidebar}>
            <div className={css.sticky}>
              <div className={css.list}>
                <h5 className={css.sidebar_heading}>Тематика</h5>
                <p className={css.sidebar_text}>{post.sections[0].name}</p>

                <h5 className={css.sidebar_heading}>Дата публикации</h5>
                <p className={css.sidebar_text}>
                  {getDateString(new Date(post.activeFrom))}
                </p>
              </div>
              <Share
                link={`${process.env.NEXT_PUBLIC_DOMAIN_ENDPOINT}/${link}`}
              />
            </div>
          </aside>
        </div>
      </MaxWidth>

      {products?.length > 0 && (
        <MaxWidth>
          <h4 className={css.productsTitle}>Товары из статьи</h4>
          <ul className={css.productsList}>
            {products.map((product) => (
              <Card
                key={product.id}
                kind="grid"
                element={product}
                footerClassName={css.cardFooter}
                category={product.section}
              />
            ))}
          </ul>
        </MaxWidth>
      )}

      <ReadMore />
    </section>
  )
}

export default JournalDetailPost
